/* eslint-disable max-lines */
/**
 * AdminFileTree
 *
 * Unified file-manager tree for the Slide Sense admin tab.
 *
 * Shows folders and decks in a single hierarchical view — similar to
 * VS Code's explorer sidebar. Inline hover actions let admins create
 * subfolders, upload decks, and delete items without leaving the tree.
 * Right-click context menus provide the full set of actions.
 */

import type React from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { useQueryClient } from "@tanstack/react-query"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { DeckStatusResponse } from "../admin-api"
import type {
  BulkImportItem,
  BulkImportJobResponse,
  BulkImportPreflight,
  FolderDeletePreview,
  SlideSenseDeck,
  SlideSenseFolder,
  SlideSenseSlide,
  UsageTreeMap,
} from "../types"
import type { BulkImportManifest } from "../utils/bulk-import"

import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Eye,
  FileText,
  Folder,
  FolderOpen,
  FolderPlus,
  FolderUp,
  GripVertical,
  Loader2,
  Pencil,
  RotateCcw,
  Trash2,
  Upload,
  User,
} from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Progress } from "@/components/ui/progress"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import {
  createBulkImport,
  createFolder,
  deleteDeck,
  deleteFolder,
  fetchBulkImport,
  fetchDeckCovers,
  fetchDeckStatus,
  getFolderDeletePreview,
  getUploadDuplicateInfo,
  preflightBulkImport,
  reprocessDeck,
  updateDeck,
  updateFolder,
  uploadBulkImportFile,
  uploadDeck,
} from "../admin-api"
import { fetchSlides } from "../api"
import { evictDeckSlideCaches } from "../slide-sense-slide-cache"
import { waitForDeckProcessing } from "../wait-for-deck-processing"
import { SlideCategoryBadge } from "./SlideCategoryBadge"
import { SlideThumbnailImage } from "./SlideThumbnailImage"
import {
  buildBulkImportManifest,
  normalizeManifestPath,
  updateBulkImportItemStatus,
} from "../utils/bulk-import"
import { countPptxSlides, LARGE_DECK_SLIDE_THRESHOLD } from "../utils/count-pptx-slides"
import { UsageNodeDrawer } from "./usage/UsageNodeDrawer"
import { useUsageTree } from "../hooks/use-slide-sense-usage"
import { buildTree, type FolderNode } from "../utils/folder-tree"

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface AdminFileTreeProps {
  folders: SlideSenseFolder[]
  decks: SlideSenseDeck[]
  onRefresh: () => void
  /** Bulk import affordances are hidden unless the caller is in the
   *  `slide_sense_super_admins` group. Default false so misconfigured
   *  callers can't accidentally expose the action. */
  isSuperAdmin?: boolean
}

/** Index decks by folder_id for O(1) lookup. */
function buildDeckMap(decks: SlideSenseDeck[]): Map<string, SlideSenseDeck[]> {
  const map = new Map<string, SlideSenseDeck[]>()
  for (const d of decks) {
    if (!map.has(d.folder_id)) map.set(d.folder_id, [])
    map.get(d.folder_id)!.push(d)
  }
  // Match the user-facing order: admin-defined sort_order ASC, newest-first as
  // the tie-breaker (mirrors list_decks on the backend).
  for (const group of map.values()) {
    group.sort(
      (a, b) =>
        (a.sort_order ?? 0) - (b.sort_order ?? 0) || b.created_at.localeCompare(a.created_at)
    )
  }
  return map
}

type BulkImportDialogState = {
  targetFolderId: string | null
  manifest: BulkImportManifest
  fileByPath: Map<string, File>
  preflight: BulkImportPreflight
  job?: BulkImportJobResponse
  phase: "preflight" | "uploading" | "processing" | "completed"
  uploaded: number
  uploadTotal: number
}

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const STATUS_ICON: Record<SlideSenseDeck["processing_status"], React.ReactNode> = {
  completed: <CheckCircle2 className="h-3.5 w-3.5" />,
  processing: <Loader2 className="text-primary h-3.5 w-3.5 animate-spin" />,
  pending: <Loader2 className="text-muted-foreground h-3.5 w-3.5 animate-spin" />,
  failed: <AlertCircle className="text-destructive h-3.5 w-3.5" />,
  completed_with_errors: <AlertCircle className="h-3.5 w-3.5" />,
}

const STATUS_LABEL_KEY: Record<SlideSenseDeck["processing_status"], string> = {
  completed: "slideSense.statusCompleted",
  processing: "slideSense.statusProcessing",
  pending: "slideSense.statusPending",
  failed: "slideSense.statusFailed",
  completed_with_errors: "slideSense.statusPartial",
}

// ---------------------------------------------------------------------------
// Context menu types & component
// ---------------------------------------------------------------------------

interface ContextMenuState {
  x: number
  y: number
  type: "folder" | "deck"
  item: SlideSenseFolder | SlideSenseDeck
}

interface ContextMenuItem {
  label: string
  icon: React.ReactNode
  onClick: () => void
  destructive?: boolean
}

function ContextMenu({
  menu,
  items,
  onClose,
}: {
  menu: ContextMenuState
  items: ContextMenuItem[]
  onClose: () => void
}) {
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }

    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("keydown", handleEscape)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleEscape)
    }
  }, [onClose])

  // Adjust position so the menu doesn't overflow the viewport
  const adjustedX = Math.min(menu.x, window.innerWidth - 180)
  const adjustedY = Math.min(menu.y, window.innerHeight - items.length * 32 - 16)

  // Portal to <body> so position:fixed is anchored to the viewport even when
  // an ancestor has transform/filter/perspective (which creates a containing
  // block that would otherwise trap fixed positioning).
  return createPortal(
    <div
      ref={menuRef}
      className="bg-popover fixed z-50 min-w-[160px] rounded-md border p-1 shadow-md"
      style={{ left: adjustedX, top: adjustedY }}
    >
      {items.map((item) => (
        <button
          key={item.label}
          type="button"
          className={`hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors ${
            item.destructive ? "text-destructive hover:text-destructive" : "text-foreground"
          }`}
          onClick={() => {
            item.onClick()
            onClose()
          }}
        >
          {item.icon}
          {item.label}
        </button>
      ))}
    </div>,
    document.body
  )
}

// ---------------------------------------------------------------------------
// Status badge variant helper
// ---------------------------------------------------------------------------

const STATUS_VARIANT: Record<
  SlideSenseDeck["processing_status"],
  "default" | "secondary" | "destructive" | "outline" | "success" | "warning"
> = {
  completed: "success",
  processing: "secondary",
  pending: "outline",
  failed: "destructive",
  completed_with_errors: "warning",
}

// ---------------------------------------------------------------------------
// DeckTile — card-style deck display inside a folder
// ---------------------------------------------------------------------------

function DeckTile({
  deck,
  coverSlideId,
  onRename,
  onDelete,
  onRetry,
  onContextMenu,
  onView,
  onOpenDrawer,
  usageData,
}: {
  deck: SlideSenseDeck
  coverSlideId?: string
  onRename: (deck: SlideSenseDeck) => void
  onDelete: (deck: SlideSenseDeck) => void
  onRetry: (deck: SlideSenseDeck) => void
  onContextMenu: (e: React.MouseEvent, deck: SlideSenseDeck) => void
  onView?: (deck: SlideSenseDeck) => void
  onOpenDrawer?: (deck: SlideSenseDeck) => void
  usageData?: UsageTreeMap
}) {
  const { t, i18n } = useTranslation()
  const isProcessing =
    deck.processing_status === "processing" || deck.processing_status === "pending"
  // Retry/reprocess for failures, partial successes, and completed decks.
  // Completed decks use the same backend endpoint but get a confirmation
  // dialog first (re-ingest deletes all slide rows and assigns new ids).
  const canRetry =
    deck.processing_status === "failed" ||
    deck.processing_status === "completed_with_errors" ||
    deck.processing_status === "completed"
  const retryLabelKey =
    deck.processing_status === "completed" ? "slideSense.reprocess" : "slideSense.retry"
  const date = new Date(deck.created_at)
  const dateStr = date.toLocaleDateString(i18n.language, { month: "short", day: "numeric" })

  // dnd-kit sortable wiring — only the GripVertical handle starts a drag, so
  // the card's view/rename/delete/context-menu actions keep working. Decks
  // reorder within their folder only (siblings share the same folder_id).
  const sortable = useSortable({ id: deck.id })
  const sortableStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.4 : undefined,
  }

  return (
    <div
      ref={sortable.setNodeRef}
      className="group/deck border-border/45 bg-background/10 hover:border-border/70 relative flex flex-col overflow-hidden rounded-lg border shadow-sm transition-[border-color,background-color,box-shadow] duration-200 hover:shadow-md"
      style={sortableStyle}
      onContextMenu={(e) => {
        e.preventDefault()
        onContextMenu(e, deck)
      }}
    >
      {/* Drag handle — top-left, appears on hover. stopPropagation so grabbing
          it never triggers the cover/open actions underneath. */}
      {/* dnd-kit's attributes give this handle role="button" + tabIndex; the
          KeyboardSensor (Space/Enter to lift, arrows to move) makes it operable
          from the keyboard. The click handler only stops propagation. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <span
        {...sortable.attributes}
        {...sortable.listeners}
        aria-label={`${t("slideSense.reorderDeck")}: ${deck.original_filename}`}
        className="text-foreground/70 focus-visible:ring-ring absolute top-1.5 left-1.5 z-10 flex h-6 w-6 cursor-grab items-center justify-center rounded-md bg-white/85 opacity-0 shadow-sm transition-opacity group-hover/deck:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:outline-none active:cursor-grabbing"
        title={t("slideSense.reorderDeck")}
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </span>
      {/* Cover thumbnail — 16:9 aspect ratio */}
      <div className="bg-muted/40 relative aspect-[16/9] w-full overflow-hidden">
        <SlideThumbnailImage
          fallbackIcon={<FileText className="text-muted-foreground/30 h-8 w-8" />}
          slideId={coverSlideId}
        />

        {/* Status + slide count badges */}
        <div className="absolute top-1.5 right-1.5 flex flex-col items-end gap-1">
          <Badge
            className="gap-1 px-1.5 text-[10px]"
            variant={STATUS_VARIANT[deck.processing_status]}
          >
            {STATUS_ICON[deck.processing_status]}
            {t(STATUS_LABEL_KEY[deck.processing_status])}
          </Badge>
          {deck.slide_count > 0 && (
            <Badge className="text-[10px]" variant="secondary">
              {t("slideSense.slideCount", { count: deck.slide_count })}
            </Badge>
          )}
        </div>

        {/* Processing progress overlay */}
        {isProcessing && deck.processing_progress > 0 && (
          <div className="absolute right-0 bottom-0 left-0">
            <Progress className="h-1 rounded-none" value={deck.processing_progress} />
          </div>
        )}

        {/* Hover overlay with actions — stacked vertically so 3-4 buttons
            always fit inside the 220px-min card width. Horizontal layout
            clipped "Delete" once the Retry button was added. */}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/40 opacity-0 transition-opacity duration-200 group-hover/deck:opacity-100">
          {onView && deck.processing_status === "completed" && (
            <button
              className="text-foreground flex h-8 w-40 items-center justify-center gap-1.5 rounded-md bg-white/90 px-3 text-xs font-medium shadow-sm hover:bg-white"
              type="button"
              onClick={() => onView(deck)}
            >
              <Eye className="h-3.5 w-3.5" />
              {t("slideSense.viewSlides")}
            </button>
          )}
          <button
            className="text-foreground flex h-8 w-40 items-center justify-center gap-1.5 rounded-md bg-white/90 px-3 text-xs font-medium shadow-sm hover:bg-white"
            type="button"
            onClick={() => onRename(deck)}
          >
            <Pencil className="h-3.5 w-3.5" />
            {t("slideSense.rename")}
          </button>
          {canRetry && (
            <button
              className="text-foreground flex h-8 w-40 items-center justify-center gap-1.5 rounded-md bg-white/90 px-3 text-xs font-medium shadow-sm hover:bg-white"
              type="button"
              onClick={() => onRetry(deck)}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {t(retryLabelKey)}
            </button>
          )}
          <button
            className="text-destructive flex h-8 w-40 items-center justify-center gap-1.5 rounded-md bg-white/90 px-3 text-xs font-medium shadow-sm hover:bg-white"
            type="button"
            onClick={() => onDelete(deck)}
          >
            <Trash2 className="h-3.5 w-3.5" />
            {t("slideSense.delete")}
          </button>
        </div>
      </div>

      {/* Card body */}
      <div className="flex flex-col gap-1 p-2.5">
        <div className="flex items-center gap-1">
          <button
            className="min-w-0 cursor-pointer truncate border-0 bg-transparent p-0 text-left text-sm leading-snug font-medium hover:underline"
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onOpenDrawer?.(deck)
            }}
          >
            {deck.original_filename}
          </button>
          {usageData?.decks[deck.id]?.uses ? (
            <span
              className="text-muted-foreground ml-auto inline-flex shrink-0 items-center gap-1 text-xs tabular-nums"
              title={t("slideSense.usage.kpi.uses")}
            >
              <Activity aria-hidden="true" className="h-3 w-3" />
              {usageData.decks[deck.id].uses}
            </span>
          ) : null}
        </div>
        <div className="text-muted-foreground flex items-center gap-3 text-[10px]">
          <span className="flex items-center gap-1 truncate">
            <User className="h-3 w-3 shrink-0" />
            {deck.uploaded_by_name || t("slideSense.categoryOther")}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3 shrink-0" />
            {dateStr}
          </span>
        </div>
      </div>
    </div>
  )
}

// For backwards compat — DeckRow as simpler text-based display for orphaned decks
function DeckRow({
  deck,
  level,
  onDelete,
  onContextMenu,
}: {
  deck: SlideSenseDeck
  level: number
  onDelete: (deck: SlideSenseDeck) => void
  onContextMenu: (e: React.MouseEvent, deck: SlideSenseDeck) => void
}) {
  return (
    <div
      className="group/deck hover:bg-accent/50 flex items-center gap-1 py-1.5 pr-2 transition-colors"
      style={{ paddingLeft: `${level * 28 + 24}px` }}
      onContextMenu={(e) => {
        e.preventDefault()
        onContextMenu(e, deck)
      }}
    >
      <FileText className="text-muted-foreground/70 h-3.5 w-3.5 shrink-0" />
      <span className="text-foreground/90 min-w-0 flex-1 truncate text-sm">
        {deck.original_filename}
      </span>
      <span className="shrink-0">{STATUS_ICON[deck.processing_status]}</span>
      <button
        className="text-muted-foreground hover:text-destructive -m-1 flex size-6 shrink-0 items-center justify-center rounded-sm opacity-0 transition-opacity group-hover/deck:opacity-100"
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onDelete(deck)
        }}
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// FolderRow — recursive folder node with inline actions
// ---------------------------------------------------------------------------

function FolderRow({
  node,
  level,
  deckMap,
  expandedIds,
  onToggleExpand,
  onCreateSubfolder,
  onUploadTrigger,
  onDeleteFolder,
  onRenameDeck,
  onRenameFolder,
  onDeleteDeck,
  onRetryDeck,
  onContextMenuFolder,
  onContextMenuDeck,
  creatingInFolderId,
  newSubfolderName,
  onNewSubfolderNameChange,
  onConfirmCreateSubfolder,
  onCancelCreateSubfolder,
  uploadingFolderId,
  onFileDrop,
  deckCovers,
  onViewDeck,
  onOpenDrawer,
  usageData,
}: {
  node: FolderNode
  level: number
  deckMap: Map<string, SlideSenseDeck[]>
  expandedIds: Set<string>
  onToggleExpand: (id: string) => void
  onCreateSubfolder: (parentId: string) => void
  onUploadTrigger: (folderId: string) => void
  onDeleteFolder: (folder: SlideSenseFolder) => void
  onRenameDeck: (deck: SlideSenseDeck) => void
  onRenameFolder: (folder: SlideSenseFolder) => void
  onDeleteDeck: (deck: SlideSenseDeck) => void
  onRetryDeck: (deck: SlideSenseDeck) => void
  onContextMenuFolder: (e: React.MouseEvent, folder: SlideSenseFolder) => void
  onContextMenuDeck: (e: React.MouseEvent, deck: SlideSenseDeck) => void
  /** Folder id currently creating a subfolder in (shows inline input) */
  creatingInFolderId: string | null
  newSubfolderName: string
  onNewSubfolderNameChange: (val: string) => void
  onConfirmCreateSubfolder: () => void
  onCancelCreateSubfolder: () => void
  /** Folder id currently being uploaded to (shows spinner) */
  uploadingFolderId: string | null
  /** Native file drop handler */
  onFileDrop: (folderId: string, file: File) => void
  /** Deck cover slide IDs map (deck_id -> first_slide_id) */
  deckCovers: Record<string, string>
  onViewDeck: (deck: SlideSenseDeck) => void
  onOpenDrawer: (type: "folder" | "deck", id: string) => void
  /** Usage tree data for inline badges */
  usageData?: UsageTreeMap
}) {
  const { folder, children } = node
  const { t } = useTranslation()
  const [isDragOver, setIsDragOver] = useState(false)
  const folderDecks = deckMap.get(folder.id) ?? []
  const hasContent = children.length > 0 || folderDecks.length > 0
  const isExpanded = expandedIds.has(folder.id)
  const isCreatingHere = creatingInFolderId === folder.id
  const isUploadingHere = uploadingFolderId === folder.id
  const subfolderInputRef = useRef<HTMLInputElement>(null)

  // dnd-kit sortable wiring — the drag handle (GripVertical icon below) is
  // the only thing that picks up the drag, so the row's normal click/expand
  // behaviour stays intact.
  const sortable = useSortable({ id: folder.id })
  const sortableStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.4 : undefined,
  }

  // Auto-focus the inline input when it appears
  useEffect(() => {
    if (isCreatingHere) {
      subfolderInputRef.current?.focus()
    }
  }, [isCreatingHere])

  const childIds = children.map((c) => c.folder.id)

  return (
    <div ref={sortable.setNodeRef} style={sortableStyle}>
      {/* Folder header row */}
      <div
        role="presentation"
        style={{ paddingLeft: `${level * 28}px` }}
        className={`group/folder hover:bg-accent/50 flex cursor-pointer items-center gap-0.5 py-1.5 pr-2 transition-colors ${
          isDragOver ? "bg-primary/10 ring-primary/30 ring-2 ring-inset" : ""
        }`}
        onClick={() => onToggleExpand(folder.id)}
        onDragLeave={() => setIsDragOver(false)}
        onContextMenu={(e) => {
          e.preventDefault()
          onContextMenuFolder(e, folder)
        }}
        onDragOver={(e) => {
          // Only accept file drags (not dnd-kit internal drags)
          if (e.dataTransfer.types.includes("Files")) {
            e.preventDefault()
            e.dataTransfer.dropEffect = "copy"
            setIsDragOver(true)
          }
        }}
        onDrop={(e) => {
          e.preventDefault()
          setIsDragOver(false)
          if (e.dataTransfer.files.length === 0) return
          const file = e.dataTransfer.files[0]
          if (file.name.endsWith(".pptx")) {
            onFileDrop(folder.id, file)
          } else {
            toast.error(t("slideSense.onlyPptx"))
          }
        }}
      >
        {/* Drag handle — admins can grab this to reorder sibling folders.
            stopPropagation on click so it doesn't toggle the row. */}
        {/* dnd-kit's attributes give this handle role="button" + tabIndex; the
            KeyboardSensor (Space/Enter to lift, arrows to move) makes it operable
            from the keyboard. The click handler only stops propagation. */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <span
          {...sortable.attributes}
          {...sortable.listeners}
          aria-label={`${t("slideSense.reorderFolder")}: ${folder.name}`}
          className="text-muted-foreground/40 hover:text-foreground focus-visible:ring-ring flex h-6 w-6 shrink-0 cursor-grab items-center justify-center opacity-0 transition-opacity group-hover/folder:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:outline-none active:cursor-grabbing"
          title={t("slideSense.reorderFolder")}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </span>

        {/* Chevron — the keyboard-accessible expand/collapse control. The row's
            own onClick is a mouse convenience only. */}
        <button
          aria-expanded={isExpanded}
          aria-hidden={!(hasContent || isCreatingHere)}
          aria-label={`${isExpanded ? t("slideSense.collapseFolder") : t("slideSense.expandFolder")}: ${folder.name}`}
          className="text-muted-foreground focus-visible:ring-ring -mx-0.5 flex size-6 shrink-0 items-center justify-center rounded-sm focus-visible:ring-2 focus-visible:outline-none"
          tabIndex={hasContent || isCreatingHere ? 0 : -1}
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onToggleExpand(folder.id)
          }}
        >
          {!(hasContent || isCreatingHere) && <ChevronRight className="h-3.5 w-3.5 opacity-0" />}
          {(hasContent || isCreatingHere) && isExpanded && <ChevronDown className="h-3.5 w-3.5" />}
          {(hasContent || isCreatingHere) && !isExpanded && (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
        </button>

        {/* Folder icon */}
        {isExpanded ? (
          <FolderOpen className="text-warning-ink h-[18px] w-[18px] shrink-0" />
        ) : (
          <Folder className="text-warning-ink/80 h-[18px] w-[18px] shrink-0" />
        )}

        {/* Folder name */}
        <span
          className="text-foreground/90 focus-visible:ring-ring -my-0.5 ml-1 min-w-0 cursor-pointer truncate rounded-sm py-0.5 text-sm font-medium hover:underline focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation()
            onOpenDrawer("folder", folder.id)
          }}
          onKeyDown={(e) => {
            e.stopPropagation()
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              onOpenDrawer("folder", folder.id)
            }
          }}
        >
          {folder.name}
        </span>

        {/* Deck count badge */}
        {folderDecks.length > 0 && (
          <span className="bg-muted text-muted-foreground ml-1 shrink-0 rounded-md px-1.5 py-0.5 text-[10px] tabular-nums">
            {folderDecks.length}
          </span>
        )}

        {/* Inline action buttons (visible on hover) — sit right after the
            folder name + count, not pushed to the right edge. */}
        <div className="ml-1 flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover/folder:opacity-100">
          {/* Create subfolder — kept compact (dense tree row, ~32px tall)
              rather than the 40px minimum, but carries a focus-visible ring
              and a themed keyboard-accessible tooltip. Bumped 16px -> 20px,
              matching the row's chevron/drag-handle sizing, since that's as
              far as the row height allows. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                aria-label={t("slideSense.createSubfolderIn", { name: folder.name })}
                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring -m-0.5 flex size-6 items-center justify-center rounded-sm focus-visible:ring-2 focus-visible:outline-none"
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onCreateSubfolder(folder.id)
                }}
              >
                <FolderPlus className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{t("slideSense.newSubfolder")}</TooltipContent>
          </Tooltip>

          {/* Upload to this folder — same compact-tree carve-out as above. */}
          <Tooltip>
            <TooltipTrigger asChild>
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Radix tooltip-on-disabled-control wrapper must stay focusable */}
              <span tabIndex={isUploadingHere ? 0 : -1}>
                <button
                  aria-label={t("slideSense.uploadDeckTo", { name: folder.name })}
                  className="text-muted-foreground hover:text-foreground focus-visible:ring-ring -m-0.5 flex size-6 items-center justify-center rounded-sm focus-visible:ring-2 focus-visible:outline-none"
                  disabled={isUploadingHere}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    onUploadTrigger(folder.id)
                  }}
                >
                  {isUploadingHere ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Upload className="h-3.5 w-3.5" />
                  )}
                </button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{t("slideSense.uploadPptx")}</TooltipContent>
          </Tooltip>
        </div>

        {/* Usage count badge — # of slide uses inside this folder over the
            active window. Stays right-aligned so the data sits separate from
            the action buttons. */}
        {usageData?.folders[folder.id]?.uses ? (
          <span
            className="text-muted-foreground ml-auto inline-flex shrink-0 items-center gap-1 text-xs tabular-nums"
            title={t("slideSense.usage.kpi.uses")}
          >
            <Activity aria-hidden="true" className="h-3 w-3" />
            {usageData.folders[folder.id].uses}
          </span>
        ) : null}
      </div>

      {/* Expanded children: decks first (most visible), then subfolders */}
      {isExpanded && (
        <div>
          {/* Deck tiles in this folder — shown above subfolders for visibility.
              Wrapped in a SortableContext so admins can drag-reorder decks
              within their folder (rect strategy for the responsive grid). */}
          {folderDecks.length > 0 && (
            <div
              className="grid gap-2 py-2"
              style={{
                paddingLeft: `${(level + 1) * 20 + 8}px`,
                paddingRight: "8px",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              }}
            >
              <SortableContext items={folderDecks.map((d) => d.id)} strategy={rectSortingStrategy}>
                {folderDecks.map((deck) => (
                  <DeckTile
                    key={deck.id}
                    coverSlideId={deckCovers[deck.id]}
                    deck={deck}
                    usageData={usageData}
                    onContextMenu={onContextMenuDeck}
                    onDelete={onDeleteDeck}
                    onOpenDrawer={(d) => onOpenDrawer("deck", d.id)}
                    onRename={onRenameDeck}
                    onRetry={onRetryDeck}
                    onView={onViewDeck}
                  />
                ))}
              </SortableContext>
            </div>
          )}

          {/* Child folders (recursive) — after decks so presentations are more
              visible. Wrapped in SortableContext so children can be reordered
              among their siblings only (no cross-parent moves). */}
          <SortableContext items={childIds} strategy={verticalListSortingStrategy}>
            {children.map((child) => (
              <FolderRow
                key={child.folder.id}
                creatingInFolderId={creatingInFolderId}
                deckCovers={deckCovers}
                deckMap={deckMap}
                expandedIds={expandedIds}
                level={level + 1}
                newSubfolderName={newSubfolderName}
                node={child}
                uploadingFolderId={uploadingFolderId}
                usageData={usageData}
                onCancelCreateSubfolder={onCancelCreateSubfolder}
                onConfirmCreateSubfolder={onConfirmCreateSubfolder}
                onContextMenuDeck={onContextMenuDeck}
                onContextMenuFolder={onContextMenuFolder}
                onCreateSubfolder={onCreateSubfolder}
                onDeleteDeck={onDeleteDeck}
                onDeleteFolder={onDeleteFolder}
                onFileDrop={onFileDrop}
                onNewSubfolderNameChange={onNewSubfolderNameChange}
                onOpenDrawer={onOpenDrawer}
                onRenameDeck={onRenameDeck}
                onRenameFolder={onRenameFolder}
                onRetryDeck={onRetryDeck}
                onToggleExpand={onToggleExpand}
                onUploadTrigger={onUploadTrigger}
                onViewDeck={onViewDeck}
              />
            ))}
          </SortableContext>

          {/* Inline subfolder creation input */}
          {isCreatingHere && (
            <div
              className="flex items-center gap-1.5 py-1 pr-2"
              style={{ paddingLeft: `${(level + 1) * 20}px` }}
            >
              <FolderPlus className="text-warning-ink/60 h-3.5 w-3.5 shrink-0" />
              <Input
                ref={subfolderInputRef}
                // `focus-visible:ring-1` pins the width: these 24px inline
                // rename fields own their focus affordance (a compact 1px
                // primary ring), so they must not inherit the 3px glow.
                className="ring-primary/40 focus-visible:ring-primary h-6 flex-1 border-none bg-transparent px-1 py-0 text-sm shadow-none ring-1 focus-visible:ring-1"
                maxLength={100}
                placeholder={t("slideSense.folderName")}
                value={newSubfolderName}
                onChange={(e) => onNewSubfolderNameChange(e.target.value)}
                onBlur={() => {
                  // Small delay to allow click on confirm to register
                  setTimeout(() => {
                    if (!newSubfolderName.trim()) onCancelCreateSubfolder()
                  }, 150)
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onConfirmCreateSubfolder()
                  if (e.key === "Escape") onCancelCreateSubfolder()
                }}
              />
              <button
                className="text-primary text-[11px] font-medium hover:underline"
                type="button"
                onClick={onConfirmCreateSubfolder}
              >
                {t("slideSense.create")}
              </button>
              <button
                className="text-muted-foreground text-[11px] hover:underline"
                type="button"
                onClick={onCancelCreateSubfolder}
              >
                {t("slideSense.cancel")}
              </button>
            </div>
          )}

          {/* Empty folder placeholder — doubles as drop hint */}
          {children.length === 0 && folderDecks.length === 0 && !isCreatingHere && (
            <div
              style={{ paddingLeft: `${(level + 1) * 20 + 8}px` }}
              className={`text-muted-foreground/60 flex items-center gap-1.5 py-2 text-[11px] italic ${
                isDragOver ? "text-primary/60" : ""
              }`}
            >
              <Upload className="h-3.5 w-3.5" />
              {t("slideSense.emptyFolder")}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AdminFileTree({
  folders,
  decks,
  onRefresh,
  isSuperAdmin = false,
}: AdminFileTreeProps) {
  const { t } = useTranslation()
  const { data: usageData } = useUsageTree("30d")
  const queryClient = useQueryClient()

  // ---- Folder drag-to-reorder ------------------------------------------------
  // Mirrors the sidebar's pattern (SlideSenseSidebar.tsx): siblings-only
  // reordering with comfortable sort_order spacing so future single-item moves
  // don't require rewriting every row.
  //
  // `optimisticOrder` is a sort_order overlay applied on top of the `folders`
  // prop. We need this on top of the React Query cache write because the
  // admin tab receives folders through a parent-owned useState (loadAdminData),
  // not directly from useFolders() — so without a local overlay the row would
  // visibly snap back to its source position while the PUT is in flight.
  //
  // The overlay clears when the prop arrives with the new sort_order baked in
  // (after onRefresh / loadAdminData fetches fresh data), or on PUT failure.
  const [optimisticOrder, setOptimisticOrder] = useState<Map<string, number> | null>(null)

  const effectiveFolders = useMemo(() => {
    if (!optimisticOrder) return folders
    return folders.map((f) =>
      optimisticOrder.has(f.id) ? { ...f, sort_order: optimisticOrder.get(f.id)! } : f
    )
  }, [folders, optimisticOrder])

  // Clear the overlay once the parent prop catches up. We can't compare values
  // reliably (the server may snap to its own spacing), so we just clear
  // whenever the prop reference changes — the rendered order is governed by
  // the (now-correct) prop data once we get past this point. Intentionally
  // omits `optimisticOrder` from deps; including it would clear the overlay
  // the instant we set it.
  const foldersRef = useRef(folders)
  useEffect(() => {
    if (foldersRef.current !== folders) {
      foldersRef.current = folders
      setOptimisticOrder(null)
    }
  }, [folders])

  const tree = useMemo(() => buildTree(effectiveFolders), [effectiveFolders])
  const rootIds = useMemo(() => tree.map((n) => n.folder.id), [tree])
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleFolderDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      const activeId = String(active.id)
      const overId = String(over.id)
      const activeFolder = effectiveFolders.find((f) => f.id === activeId)
      const overFolder = effectiveFolders.find((f) => f.id === overId)
      if (!activeFolder || !overFolder) return
      if (activeFolder.parent_folder_id !== overFolder.parent_folder_id) {
        // Reparenting is intentionally out of scope; ignore cross-parent drops.
        return
      }

      const siblings = effectiveFolders
        .filter((f) => f.parent_folder_id === activeFolder.parent_folder_id)
        .sort((a, b) => a.sort_order - b.sort_order)
      const oldIndex = siblings.findIndex((f) => f.id === activeId)
      const newIndex = siblings.findIndex((f) => f.id === overId)
      if (oldIndex === -1 || newIndex === -1) return

      const reordered = arrayMove(siblings, oldIndex, newIndex)
      const updates = reordered.map((f, idx) => ({ id: f.id, sort_order: (idx + 1) * 100 }))
      const changed = updates.filter((u) => {
        const original = effectiveFolders.find((f) => f.id === u.id)
        return original?.sort_order !== u.sort_order
      })
      if (changed.length === 0) return

      // Optimistic overlay so the dragged row stays in its dropped position
      // until the parent re-fetches with the new server state.
      const newOrderById = new Map(updates.map((u) => [u.id, u.sort_order]))
      setOptimisticOrder(newOrderById)

      // Also update the sidebar's React Query cache so the (separately-mounted)
      // user-facing folder nav stays in sync without waiting for an explicit
      // refetch there.
      queryClient.setQueryData<SlideSenseFolder[]>(["slide-sense", "folders"], (old) =>
        old?.map((f) =>
          newOrderById.has(f.id) ? { ...f, sort_order: newOrderById.get(f.id)! } : f
        )
      )

      try {
        await Promise.all(changed.map((u) => updateFolder(u.id, { sort_order: u.sort_order })))
        onRefresh()
      } catch (err) {
        console.error("Folder reorder failed:", err)
        toast.error(t("slideSense.folderReorderFailed"))
        setOptimisticOrder(null)
        // Surface the server-truth ordering in both surfaces.
        await queryClient.invalidateQueries({ queryKey: ["slide-sense", "folders"] })
        onRefresh()
      }
    },
    [effectiveFolders, queryClient, onRefresh, t]
  )

  // ---- Deck drag-to-reorder --------------------------------------------------
  // Same shape as the folder reorder above, scoped to one folder's decks. The
  // overlay keeps the dropped card in place until the parent re-fetches.
  const [optimisticDeckOrder, setOptimisticDeckOrder] = useState<Map<string, number> | null>(null)

  const effectiveDecks = useMemo(() => {
    if (!optimisticDeckOrder) return decks
    return decks.map((d) =>
      optimisticDeckOrder.has(d.id) ? { ...d, sort_order: optimisticDeckOrder.get(d.id)! } : d
    )
  }, [decks, optimisticDeckOrder])

  const decksRef = useRef(decks)
  useEffect(() => {
    if (decksRef.current !== decks) {
      decksRef.current = decks
      setOptimisticDeckOrder(null)
    }
  }, [decks])

  const deckMap = useMemo(() => buildDeckMap(effectiveDecks), [effectiveDecks])

  const handleDeckDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      const activeId = String(active.id)
      const overId = String(over.id)
      const activeDeck = effectiveDecks.find((d) => d.id === activeId)
      const overDeck = effectiveDecks.find((d) => d.id === overId)
      if (!activeDeck || !overDeck) return
      // Reordering is intra-folder only; ignore drops onto another folder's deck.
      if (activeDeck.folder_id !== overDeck.folder_id) return

      const siblings = effectiveDecks
        .filter((d) => d.folder_id === activeDeck.folder_id)
        .sort(
          (a, b) =>
            (a.sort_order ?? 0) - (b.sort_order ?? 0) || b.created_at.localeCompare(a.created_at)
        )
      const oldIndex = siblings.findIndex((d) => d.id === activeId)
      const newIndex = siblings.findIndex((d) => d.id === overId)
      if (oldIndex === -1 || newIndex === -1) return

      const reordered = arrayMove(siblings, oldIndex, newIndex)
      const updates = reordered.map((d, idx) => ({ id: d.id, sort_order: (idx + 1) * 100 }))
      const changed = updates.filter((u) => {
        const original = effectiveDecks.find((d) => d.id === u.id)
        return (original?.sort_order ?? 0) !== u.sort_order
      })
      if (changed.length === 0) return

      const newOrderById = new Map(updates.map((u) => [u.id, u.sort_order]))
      setOptimisticDeckOrder(newOrderById)
      // Push the new order into the React Query cache that the (separately
      // mounted) user-facing DeckBrowser reads from, so it doesn't keep
      // showing the old order until its 30s staleTime lapses. Mirrors the
      // folder handler's cache sync. The "decks" key is folder-scoped, so
      // patch every cached deck list.
      queryClient.setQueriesData<SlideSenseDeck[]>({ queryKey: ["slide-sense", "decks"] }, (old) =>
        old?.map((d) =>
          newOrderById.has(d.id) ? { ...d, sort_order: newOrderById.get(d.id)! } : d
        )
      )

      try {
        await Promise.all(changed.map((u) => updateDeck(u.id, { sort_order: u.sort_order })))
        onRefresh()
      } catch (err) {
        console.error("Deck reorder failed:", err)
        toast.error(t("slideSense.deckReorderFailed"))
        setOptimisticDeckOrder(null)
        await queryClient.invalidateQueries({ queryKey: ["slide-sense", "decks"] })
        onRefresh()
      }
    },
    [effectiveDecks, queryClient, onRefresh, t]
  )

  // Dispatch a drag to the right handler: deck ids resolve to a deck, folder
  // ids don't. Both SortableContexts live under one DndContext.
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const id = String(event.active.id)
      if (effectiveDecks.some((d) => d.id === id)) void handleDeckDragEnd(event)
      else void handleFolderDragEnd(event)
    },
    [effectiveDecks, handleDeckDragEnd, handleFolderDragEnd]
  )

  // ---- Usage node drawer state -----------------------------------------------
  const [drawer, setDrawer] = useState<{ type: "folder" | "deck" | "slide"; id: string } | null>(
    null
  )

  const handleOpenDrawer = useCallback((type: "folder" | "deck", id: string) => {
    setDrawer({ type, id })
  }, [])

  // ---- Deck slide viewer state -----------------------------------------------
  const [viewingDeck, setViewingDeck] = useState<SlideSenseDeck | null>(null)
  const [deckSlides, setDeckSlides] = useState<SlideSenseSlide[]>([])
  const [isDeckSlidesLoading, setIsDeckSlidesLoading] = useState(false)

  const handleViewDeck = useCallback(async (deck: SlideSenseDeck) => {
    setViewingDeck(deck)
    setDeckSlides([])
    setIsDeckSlidesLoading(true)
    try {
      const slides = await fetchSlides({ deck_id: deck.id, page_size: 100 })
      const sorted = [...slides].sort((a, b) => a.slide_index - b.slide_index)
      setDeckSlides(sorted)
    } catch (err) {
      console.error("Operation failed:", err)
      toast.error(t("slideSense.failedToLoadSlides"))
    } finally {
      setIsDeckSlidesLoading(false)
    }
  }, [])

  // ---- Deck cover thumbnails ------------------------------------------------
  const [deckCovers, setDeckCovers] = useState<Record<string, string>>({})

  // Stable signature so a new array reference with identical contents doesn't
  // refetch — the parent passes a fresh `decks` array on every successful
  // admin-data load, which would otherwise create a request storm.
  const decksSignature = useMemo(
    () =>
      decks
        .map((d) => d.id)
        .sort()
        .join(","),
    [decks]
  )
  const coversFailedAt = useRef<number>(0)

  useEffect(() => {
    if (coversFailedAt.current && Date.now() - coversFailedAt.current < 30_000) return
    let cancelled = false
    fetchDeckCovers()
      .then((covers) => {
        if (!cancelled) setDeckCovers(covers)
      })
      .catch((err) => {
        coversFailedAt.current = Date.now()
        console.error("Failed to load deck covers:", err)
      })
    return () => {
      cancelled = true
    }
  }, [decksSignature])

  // ---- Context menu state --------------------------------------------------
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null)

  const closeContextMenu = useCallback(() => setContextMenu(null), [])

  const handleContextMenuFolder = useCallback((e: React.MouseEvent, folder: SlideSenseFolder) => {
    setContextMenu({ x: e.clientX, y: e.clientY, type: "folder", item: folder })
  }, [])

  const handleContextMenuDeck = useCallback((e: React.MouseEvent, deck: SlideSenseDeck) => {
    setContextMenu({ x: e.clientX, y: e.clientY, type: "deck", item: deck })
  }, [])

  // ---- Expand / collapse state ----------------------------------------------
  // Tree starts fully collapsed — admins toggle folders open as needed.
  // No auto-expand on folder data refetch either, so user-collapsed folders
  // stay collapsed when admin actions (rename, upload, delete) refresh the
  // list. Auto-expand still happens when the user starts an action that
  // requires the parent visible (e.g. inline subfolder creation, see
  // handleStartCreateSubfolder below).
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set<string>())

  const onToggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // ---- Inline subfolder creation -------------------------------------------
  const [creatingInFolderId, setCreatingInFolderId] = useState<string | null>(null)
  const [newSubfolderName, setNewSubfolderName] = useState("")
  const [isCreatingFolder, setIsCreatingFolder] = useState(false)

  const handleStartCreateSubfolder = useCallback((parentId: string) => {
    // Make sure the parent is expanded so the input is visible
    setExpandedIds((prev) => {
      const next = new Set(prev)
      next.add(parentId)
      return next
    })
    setCreatingInFolderId(parentId)
    setNewSubfolderName("")
  }, [])

  const handleConfirmCreateSubfolder = useCallback(async () => {
    if (!newSubfolderName.trim() || !creatingInFolderId || isCreatingFolder) return
    setIsCreatingFolder(true)
    try {
      await createFolder({
        name: newSubfolderName.trim(),
        parent_folder_id: creatingInFolderId,
      })
      toast.success(t("slideSense.folderCreated", { name: newSubfolderName.trim() }))
      setCreatingInFolderId(null)
      setNewSubfolderName("")
      onRefresh()
    } catch (err) {
      console.error("Operation failed:", err)
      toast.error(t("slideSense.failedToCreateFolder"))
    } finally {
      setIsCreatingFolder(false)
    }
  }, [newSubfolderName, creatingInFolderId, isCreatingFolder, onRefresh])

  const handleCancelCreateSubfolder = useCallback(() => {
    setCreatingInFolderId(null)
    setNewSubfolderName("")
  }, [])

  // ---- Root folder creation ------------------------------------------------
  const [showRootInput, setShowRootInput] = useState(false)
  const [rootFolderName, setRootFolderName] = useState("")
  const [isCreatingRoot, setIsCreatingRoot] = useState(false)
  const rootInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (showRootInput) rootInputRef.current?.focus()
  }, [showRootInput])

  const handleCreateRootFolder = useCallback(async () => {
    if (!rootFolderName.trim() || isCreatingRoot) return
    setIsCreatingRoot(true)
    try {
      await createFolder({ name: rootFolderName.trim(), parent_folder_id: null })
      toast.success(t("slideSense.folderCreated", { name: rootFolderName.trim() }))
      setRootFolderName("")
      setShowRootInput(false)
      onRefresh()
    } catch (err) {
      console.error("Operation failed:", err)
      toast.error(t("slideSense.failedToCreateFolder"))
    } finally {
      setIsCreatingRoot(false)
    }
  }, [rootFolderName, isCreatingRoot, onRefresh])

  // ---- Upload handling (hidden file input) ---------------------------------
  const fileInputRef = useRef<HTMLInputElement>(null)
  const bulkFolderInputRef = useRef<HTMLInputElement>(null)
  const [uploadTargetFolderId, setUploadTargetFolderId] = useState<string | null>(null)
  const [uploadingFolderId, setUploadingFolderId] = useState<string | null>(null)
  const [largeDeckPrompt, setLargeDeckPrompt] = useState<{
    file: File
    folderId: string
    slideCount: number
  } | null>(null)
  const [bulkImportTargetFolderId, setBulkImportTargetFolderId] = useState<string | null>(null)
  const [bulkImportState, setBulkImportState] = useState<BulkImportDialogState | null>(null)
  const [bulkImportBusy, setBulkImportBusy] = useState(false)
  const bulkImportPolls = useRef(new Set<ReturnType<typeof setInterval>>())

  useEffect(() => {
    const input = bulkFolderInputRef.current
    if (!input) return
    input.setAttribute("webkitdirectory", "")
    input.setAttribute("directory", "")
  }, [])

  useEffect(() => {
    const polls = bulkImportPolls.current
    return () => {
      polls.forEach((interval) => clearInterval(interval))
      polls.clear()
    }
  }, [])

  const handleUploadTrigger = useCallback((folderId: string) => {
    setUploadTargetFolderId(folderId)
    // Reset the file input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = ""
    fileInputRef.current?.click()
  }, [])

  const handleBulkImportTrigger = useCallback((folderId: string | null = null) => {
    setBulkImportTargetFolderId(folderId)
    if (bulkFolderInputRef.current) bulkFolderInputRef.current.value = ""
    bulkFolderInputRef.current?.click()
  }, [])

  const performUpload = useCallback(
    async (file: File, folderId: string) => {
      setUploadingFolderId(folderId)

      // Expand the target folder so the user sees the new deck appear
      setExpandedIds((prev) => {
        const next = new Set(prev)
        next.add(folderId)
        return next
      })

      try {
        const deck = await uploadDeck(file, folderId)
        toast.success(t("slideSense.uploaded", { name: file.name }))
        onRefresh()

        // Start polling for processing status
        pollDeckUntilDone(deck.id)
      } catch (err) {
        console.error("Operation failed:", err)
        const dup = getUploadDuplicateInfo(err)
        if (dup) {
          toast.error(
            dup.existingFilename
              ? t("slideSense.uploadDuplicate", { existingName: dup.existingFilename })
              : t("slideSense.uploadDuplicateGeneric")
          )
        } else {
          toast.error(t("slideSense.uploadFailed"))
        }
      } finally {
        setUploadingFolderId(null)
        setUploadTargetFolderId(null)
      }
    },
    [onRefresh, t]
  )

  const guardLargeDeckOrUpload = useCallback(
    async (file: File, folderId: string) => {
      const slideCount = await countPptxSlides(file)
      if (slideCount !== null && slideCount >= LARGE_DECK_SLIDE_THRESHOLD) {
        setLargeDeckPrompt({ file, folderId, slideCount })
        return
      }
      await performUpload(file, folderId)
    },
    [performUpload]
  )

  const handleFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file || !uploadTargetFolderId) return
      await guardLargeDeckOrUpload(file, uploadTargetFolderId)
    },
    [uploadTargetFolderId, guardLargeDeckOrUpload]
  )

  const handleBulkFolderSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files ?? [])
      if (selectedFiles.length === 0) return

      setBulkImportBusy(true)
      try {
        const manifest = await buildBulkImportManifest(selectedFiles)
        if (manifest.files.length === 0) {
          toast.error(t("slideSense.bulkImportNoPptx"))
          return
        }

        const fileByPath = new Map<string, File>()
        for (const file of selectedFiles) {
          if (!file.name.toLowerCase().endsWith(".pptx")) continue
          const relativePath = normalizeManifestPath(
            (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name
          )
          fileByPath.set(relativePath.toLowerCase(), file)
        }

        const preflight = await preflightBulkImport({
          target_folder_id: bulkImportTargetFolderId,
          root_folder_name: manifest.rootFolderName,
          files: manifest.files,
        })
        setBulkImportState({
          targetFolderId: bulkImportTargetFolderId,
          manifest,
          fileByPath,
          preflight,
          phase: "preflight",
          uploaded: 0,
          uploadTotal: preflight.summary.new + preflight.summary.changed,
        })
      } catch (err) {
        console.error("Bulk import preflight failed:", err)
        toast.error(t("slideSense.bulkImportPreflightFailed"))
      } finally {
        setBulkImportBusy(false)
        setBulkImportTargetFolderId(null)
      }
    },
    [bulkImportTargetFolderId, t]
  )

  const pollBulkImportUntilDone = useCallback(
    (jobId: string) => {
      let errorCount = 0
      const interval = setInterval(async () => {
        const stop = () => {
          clearInterval(interval)
          bulkImportPolls.current.delete(interval)
        }
        try {
          const next = await fetchBulkImport(jobId)
          setBulkImportState((current) =>
            current && current.job?.job.id === jobId
              ? { ...current, job: next, phase: "processing" }
              : current
          )

          if (next.job.status === "completed" || next.job.status === "completed_with_errors") {
            stop()
            setBulkImportState((current) =>
              current && current.job?.job.id === jobId
                ? { ...current, job: next, phase: "completed" }
                : current
            )
            onRefresh()
            if (next.job.status === "completed") {
              toast.success(t("slideSense.bulkImportCompleted"))
            } else {
              toast.warning(t("slideSense.bulkImportCompletedWithErrors"))
            }
          }
          errorCount = 0
        } catch (err) {
          console.error("Bulk import poll failed:", err)
          errorCount++
          if (errorCount >= 10) {
            stop()
            toast.error(t("slideSense.lostConnection"))
          }
        }
      }, 3000)
      bulkImportPolls.current.add(interval)
    },
    [onRefresh, t]
  )

  const startBulkImport = useCallback(async () => {
    if (!bulkImportState || bulkImportBusy) return
    setBulkImportBusy(true)
    try {
      const created = await createBulkImport({
        target_folder_id: bulkImportState.targetFolderId,
        root_folder_name: bulkImportState.manifest.rootFolderName,
        files: bulkImportState.manifest.files,
      })

      const uploadItems = created.items.filter(
        (item) => item.action === "new" || item.action === "changed"
      )
      setBulkImportState((current) =>
        current
          ? {
              ...current,
              job: created,
              phase: uploadItems.length ? "uploading" : "completed",
              uploadTotal: uploadItems.length,
            }
          : current
      )

      for (const item of uploadItems) {
        const file = bulkImportState.fileByPath.get(item.relative_path.toLowerCase())
        if (!file) {
          throw new Error(`Missing selected file for ${item.relative_path}`)
        }
        setBulkImportState((current) =>
          current?.job
            ? { ...current, job: updateBulkImportItemStatus(current.job, item.id, "uploading") }
            : current
        )
        await uploadBulkImportFile(created.job.id, file, item.relative_path)
        setBulkImportState((current) =>
          current?.job
            ? {
                ...current,
                job: updateBulkImportItemStatus(current.job, item.id, "queued"),
                uploaded: current.uploaded + 1,
              }
            : current
        )
      }

      if (uploadItems.length) {
        setBulkImportState((current) => (current ? { ...current, phase: "processing" } : current))
        pollBulkImportUntilDone(created.job.id)
      } else {
        onRefresh()
        toast.success(t("slideSense.bulkImportNoChanges"))
      }
    } catch (err) {
      console.error("Bulk import failed:", err)
      toast.error(t("slideSense.bulkImportFailed"))
    } finally {
      setBulkImportBusy(false)
    }
  }, [bulkImportBusy, bulkImportState, onRefresh, pollBulkImportUntilDone, t])

  /** Handle native file drop onto a folder. */
  const handleFileDrop = useCallback(
    async (folderId: string, file: File) => {
      await guardLargeDeckOrUpload(file, folderId)
    },
    [guardLargeDeckOrUpload]
  )

  // Track active polling handles per deck so we can cleanly stop them on
  // terminal state, error-cap, safety-timeout, *or* unmount. Each entry
  // holds both the recurring interval and the safety timeout, so clearing
  // one path clears the other (no orphaned 5-minute timer ticking with no
  // interval to cancel).
  type PollHandles = {
    interval: ReturnType<typeof setInterval>
    safetyTimeout: ReturnType<typeof setTimeout>
  }
  const activePolls = useRef(new Map<string, PollHandles>())

  const stopPoll = useCallback((deckId: string) => {
    const handles = activePolls.current.get(deckId)
    if (!handles) return
    clearInterval(handles.interval)
    clearTimeout(handles.safetyTimeout)
    activePolls.current.delete(deckId)
  }, [])

  useEffect(() => {
    const polls = activePolls.current
    return () => {
      polls.forEach(({ interval, safetyTimeout }) => {
        clearInterval(interval)
        clearTimeout(safetyTimeout)
      })
      polls.clear()
    }
  }, [])

  /** Poll deck status until done, then refresh the tree.
   *
   * Optional `onTerminal` callback fires once when the deck reaches a
   * terminal state. Used by the bulk-import dialog retry path to refresh
   * its own state (which is separate from the decks list onRefresh
   * updates) — without it, the dialog row stays stuck on the optimistic
   * "processing" status after a retry-then-fail.
   */
  const pollDeckUntilDone = useCallback(
    (deckId: string, onTerminal?: (status: DeckStatusResponse) => void) => {
      // Cancel any prior poll for the same deck (e.g. user uploaded then
      // re-uploaded quickly — we don't want two timers fighting).
      stopPoll(deckId)

      let errorCount = 0
      const MAX_ERRORS = 10

      const interval = setInterval(async () => {
        try {
          const status = await fetchDeckStatus(deckId)
          if (
            status.processing_status === "completed" ||
            status.processing_status === "completed_with_errors" ||
            status.processing_status === "failed"
          ) {
            stopPoll(deckId)
            onRefresh() // Only full refresh on terminal state
            evictDeckSlideCaches(deckId, status.slide_count)

            if (status.processing_status === "completed") {
              toast.success(t("slideSense.deckProcessed", { count: status.slide_count }))
            } else if (status.processing_status === "failed") {
              toast.error(t("slideSense.deckFailed"))
            }

            if (onTerminal) {
              try {
                onTerminal(status)
              } catch (cbErr) {
                console.error("pollDeckUntilDone onTerminal callback failed:", cbErr)
              }
            }
          }
          // Non-terminal: don't call onRefresh — the status endpoint is enough
          errorCount = 0
        } catch (err) {
          console.error("Deck status poll failed:", err)
          errorCount++
          if (errorCount >= MAX_ERRORS) {
            stopPoll(deckId)
            toast.error(t("slideSense.lostConnection"))
          }
        }
      }, 3000)

      // Safety: clear after 5 minutes max regardless of state.
      const safetyTimeout = setTimeout(
        () => {
          stopPoll(deckId)
        },
        5 * 60 * 1000
      )

      activePolls.current.set(deckId, { interval, safetyTimeout })
    },
    [onRefresh, stopPoll, t]
  )

  // ---- Delete handlers -----------------------------------------------------
  const [deleteTarget, setDeleteTarget] = useState<{
    folder: SlideSenseFolder
    preview: FolderDeletePreview | null
  } | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)

  const handleDeleteFolder = useCallback(
    async (folder: SlideSenseFolder) => {
      setDeleteTarget({ folder, preview: null })
      try {
        const preview = await getFolderDeletePreview(folder.id)
        setDeleteTarget((curr) =>
          curr && curr.folder.id === folder.id ? { folder, preview } : curr
        )
      } catch (err) {
        console.error("Delete preview failed:", err)
        toast.error(t("slideSense.deleteFolderPreviewFailed"))
        setDeleteTarget(null)
      }
    },
    [t]
  )

  const confirmDeleteFolder = useCallback(async () => {
    if (!deleteTarget) return
    const { folder } = deleteTarget
    setDeleteBusy(true)
    try {
      await deleteFolder(folder.id)
      toast.success(t("slideSense.folderDeleted", { name: folder.name }))
      setDeleteTarget(null)
      onRefresh()
    } catch (err) {
      console.error("Operation failed:", err)
      toast.error(t("slideSense.failedToDeleteFolder"))
    } finally {
      setDeleteBusy(false)
    }
  }, [deleteTarget, onRefresh, t])

  // ---- Rename dialog state -------------------------------------------------
  // We replace the previous `window.prompt` calls with a controlled shadcn
  // `Dialog` + `Input`. A single dialog handles both folder and deck
  // renames; the `target` discriminates which API to call on confirm.
  const [renameTarget, setRenameTarget] = useState<
    { kind: "folder"; folder: SlideSenseFolder } | { kind: "deck"; deck: SlideSenseDeck } | null
  >(null)
  const [renameValue, setRenameValue] = useState("")
  const [renameBusy, setRenameBusy] = useState(false)

  const handleRenameFolder = useCallback((folder: SlideSenseFolder) => {
    setRenameTarget({ kind: "folder", folder })
    setRenameValue(folder.name)
  }, [])

  const handleRenameDeck = useCallback((deck: SlideSenseDeck) => {
    setRenameTarget({ kind: "deck", deck })
    setRenameValue(deck.original_filename)
  }, [])

  const closeRenameDialog = useCallback(() => {
    if (renameBusy) return
    setRenameTarget(null)
    setRenameValue("")
  }, [renameBusy])

  const confirmRename = useCallback(async () => {
    if (!renameTarget) return
    const trimmed = renameValue.trim()
    if (!trimmed) return
    const currentName =
      renameTarget.kind === "folder"
        ? renameTarget.folder.name
        : renameTarget.deck.original_filename
    if (trimmed === currentName) {
      setRenameTarget(null)
      setRenameValue("")
      return
    }
    setRenameBusy(true)
    try {
      if (renameTarget.kind === "folder") {
        await updateFolder(renameTarget.folder.id, { name: trimmed })
      } else {
        await updateDeck(renameTarget.deck.id, { original_filename: trimmed })
      }
      toast.success(t("slideSense.renamedTo", { name: trimmed }))
      setRenameTarget(null)
      setRenameValue("")
      onRefresh()
    } catch (err) {
      console.error("Operation failed:", err)
      toast.error(t("slideSense.failedToRename"))
    } finally {
      setRenameBusy(false)
    }
  }, [renameTarget, renameValue, onRefresh, t])

  // ---- Delete-deck confirmation state --------------------------------------
  const [deleteDeckTarget, setDeleteDeckTarget] = useState<SlideSenseDeck | null>(null)
  const [deleteDeckBusy, setDeleteDeckBusy] = useState(false)
  const [reprocessDeckTarget, setReprocessDeckTarget] = useState<SlideSenseDeck | null>(null)
  const [reprocessDeckBusy, setReprocessDeckBusy] = useState(false)
  const [reprocessAllOpen, setReprocessAllOpen] = useState(false)
  const [reprocessAllBusy, setReprocessAllBusy] = useState(false)
  const [reprocessAllProgress, setReprocessAllProgress] = useState<{
    current: number
    total: number
    name: string
  } | null>(null)

  const handleDeleteDeck = useCallback((deck: SlideSenseDeck) => {
    setDeleteDeckTarget(deck)
  }, [])

  const confirmDeleteDeck = useCallback(async () => {
    if (!deleteDeckTarget) return
    const deck = deleteDeckTarget
    setDeleteDeckBusy(true)
    try {
      await deleteDeck(deck.id)
      toast.success(t("slideSense.deckDeleted", { name: deck.original_filename }))
      setDeleteDeckTarget(null)
      onRefresh()
    } catch (err) {
      console.error("Operation failed:", err)
      toast.error(t("slideSense.failedToDeleteDeck"))
    } finally {
      setDeleteDeckBusy(false)
    }
  }, [deleteDeckTarget, onRefresh, t])

  // ---- Retry-deck handler ---------------------------------------------------
  // Calls the existing admin reprocess endpoint, which re-downloads the
  // original PPTX from blob storage, deletes any partial slide rows, and
  // re-runs the orchestrator. The deck keeps its id, original_filename, and
  // folder_id; slides get fresh ids.
  //
  // The reprocess endpoint returns as soon as it kicks off the background
  // task, so a one-shot onRefresh() right after would snapshot the deck
  // mid-flight (status="pending") and the UI would never catch the eventual
  // terminal state. Hand off to pollDeckUntilDone — it polls the status
  // endpoint every 3s, calls onRefresh() once on terminal, and shows the
  // success/failure toast.
  const executeDeckReprocess = useCallback(
    async (deck: SlideSenseDeck, { wasCompleted = false }: { wasCompleted?: boolean } = {}) => {
      try {
        await reprocessDeck(deck.id)
        toast.success(
          t(wasCompleted ? "slideSense.reprocessStarted" : "slideSense.retryStarted", {
            name: deck.original_filename,
          })
        )
        onRefresh()
        pollDeckUntilDone(deck.id)
      } catch (err) {
        console.error("Operation failed:", err)
        toast.error(t("slideSense.retryFailed"))
      }
    },
    [onRefresh, pollDeckUntilDone, t]
  )

  const handleRetryDeck = useCallback(
    (deck: SlideSenseDeck) => {
      if (deck.processing_status === "completed") {
        setReprocessDeckTarget(deck)
        return
      }
      void executeDeckReprocess(deck)
    },
    [executeDeckReprocess]
  )

  const confirmReprocessDeck = useCallback(async () => {
    if (!reprocessDeckTarget) return
    const deck = reprocessDeckTarget
    setReprocessDeckBusy(true)
    try {
      await executeDeckReprocess(deck, { wasCompleted: true })
      setReprocessDeckTarget(null)
    } finally {
      setReprocessDeckBusy(false)
    }
  }, [executeDeckReprocess, reprocessDeckTarget])

  // ---- Bulk-import retry handlers -------------------------------------------
  // Failed bulk-import items already have a `deck_id` (the backend creates
  // the deck doc before kicking off processing), so retry is the same
  // reprocess endpoint per item. Track in-flight item ids so the dialog can
  // show per-row spinners without freezing the whole list.
  const [bulkRetryingIds, setBulkRetryingIds] = useState<Set<string>>(new Set())

  const handleRetryBulkItem = useCallback(
    async (item: BulkImportItem) => {
      const deckId = item.deck_id
      if (!deckId) {
        toast.error(t("slideSense.retryFailed"))
        return
      }
      setBulkRetryingIds((prev) => new Set(prev).add(item.id))
      try {
        await reprocessDeck(deckId)
        // Optimistically flip the row to processing so the user sees
        // immediate feedback. The pollDeckUntilDone below reconciles with
        // the real terminal status (failed / completed) once the
        // orchestrator finishes — that's what catches a second-attempt
        // failure (e.g. renderer still misconfigured), so the card and
        // the dialog row both correctly show `failed` again instead of
        // being stuck on `processing` forever.
        setBulkImportState((current) =>
          current?.job
            ? { ...current, job: updateBulkImportItemStatus(current.job, item.id, "processing") }
            : current
        )
        toast.success(t("slideSense.retryStarted", { name: item.filename }))
        onRefresh()
        // pollDeckUntilDone normally updates only the decks list. The bulk
        // dialog has its own item-status copy that wouldn't otherwise
        // catch the deck flip — re-fetch the bulk job in the onTerminal
        // callback so the dialog row transitions out of the optimistic
        // "processing" state. The GET endpoint also self-reconciles, so
        // the freshly-fetched items reflect the deck's actual status.
        const jobId = bulkImportState?.job?.job.id
        pollDeckUntilDone(deckId, () => {
          if (!jobId) return
          void (async () => {
            try {
              const next = await fetchBulkImport(jobId)
              setBulkImportState((current) =>
                current && current.job?.job.id === jobId ? { ...current, job: next } : current
              )
            } catch (fetchErr) {
              console.error("Bulk job refresh after retry failed:", fetchErr)
            }
          })()
        })
      } catch (err) {
        console.error("Operation failed:", err)
        toast.error(t("slideSense.retryFailed"))
      } finally {
        setBulkRetryingIds((prev) => {
          const next = new Set(prev)
          next.delete(item.id)
          return next
        })
      }
    },
    [onRefresh, pollDeckUntilDone, t]
  )

  const handleRetryAllFailedBulk = useCallback(async () => {
    const failed =
      bulkImportState?.job?.items.filter((it) => it.status === "failed" && it.deck_id) ?? []
    if (failed.length === 0) return
    // Run sequentially — the backend can handle parallel but a tight burst
    // of N reprocesses spikes the renderer and saturates the orchestrator's
    // bulk-render semaphore. Sequential keeps the load shape predictable.
    for (const item of failed) {
      await handleRetryBulkItem(item)
    }
  }, [bulkImportState, handleRetryBulkItem])

  const reprocessableDecks = useMemo(
    () =>
      decks.filter((d) =>
        ["completed", "completed_with_errors", "failed"].includes(d.processing_status)
      ),
    [decks]
  )

  const handleReprocessAll = useCallback(() => {
    if (reprocessableDecks.length === 0) {
      toast.error(t("slideSense.reprocessAllNone"))
      return
    }
    setReprocessAllOpen(true)
  }, [reprocessableDecks.length, t])

  const confirmReprocessAll = useCallback(async () => {
    const targets = reprocessableDecks
    if (targets.length === 0) {
      setReprocessAllOpen(false)
      return
    }
    setReprocessAllBusy(true)
    setReprocessAllProgress({ current: 0, total: targets.length, name: "" })
    let succeeded = 0
    try {
      for (let i = 0; i < targets.length; i++) {
        const deck = targets[i]
        setReprocessAllProgress({
          current: i + 1,
          total: targets.length,
          name: deck.original_filename,
        })
        await reprocessDeck(deck.id)
        onRefresh()
        const status = await waitForDeckProcessing(deck.id)
        evictDeckSlideCaches(deck.id, status.slide_count)
        if (status.processing_status === "failed") {
          toast.error(t("slideSense.deckFailed"))
          toast.error(t("slideSense.reprocessAllFailed"))
          break
        }
        succeeded += 1
      }
      if (succeeded === targets.length) {
        toast.success(t("slideSense.reprocessAllComplete", { count: succeeded }))
      }
      onRefresh()
    } catch (err) {
      console.error("Bulk reprocess failed:", err)
      toast.error(t("slideSense.reprocessAllFailed"))
    } finally {
      setReprocessAllBusy(false)
      setReprocessAllProgress(null)
      setReprocessAllOpen(false)
    }
  }, [onRefresh, reprocessableDecks, t])

  // ---- Context menu items builder -------------------------------------------
  const contextMenuItems = useMemo<ContextMenuItem[]>(() => {
    if (!contextMenu) return []

    if (contextMenu.type === "folder") {
      const folder = contextMenu.item as SlideSenseFolder
      return [
        {
          label: t("slideSense.newSubfolder"),
          icon: <FolderPlus className="h-4 w-4" />,
          onClick: () => handleStartCreateSubfolder(folder.id),
        },
        {
          label: t("slideSense.uploadPptx"),
          icon: <Upload className="h-4 w-4" />,
          onClick: () => handleUploadTrigger(folder.id),
        },
        ...(isSuperAdmin
          ? [
              {
                label: t("slideSense.bulkImportFolder"),
                icon: <FolderUp className="h-4 w-4" />,
                onClick: () => handleBulkImportTrigger(folder.id),
              } satisfies ContextMenuItem,
            ]
          : []),
        {
          label: t("slideSense.rename"),
          icon: <Pencil className="h-4 w-4" />,
          onClick: () => handleRenameFolder(folder),
        },
        {
          label: t("slideSense.delete"),
          icon: <Trash2 className="h-4 w-4" />,
          onClick: () => handleDeleteFolder(folder),
          destructive: true,
        },
      ]
    }

    // Deck context menu
    const deck = contextMenu.item as SlideSenseDeck
    const canReprocessDeck =
      deck.processing_status === "failed" ||
      deck.processing_status === "completed_with_errors" ||
      deck.processing_status === "completed"
    return [
      {
        label: t("slideSense.rename"),
        icon: <Pencil className="h-4 w-4" />,
        onClick: () => handleRenameDeck(deck),
      },
      ...(canReprocessDeck
        ? [
            {
              label: t(
                deck.processing_status === "completed" ? "slideSense.reprocess" : "slideSense.retry"
              ),
              icon: <RotateCcw className="h-4 w-4" />,
              onClick: () => handleRetryDeck(deck),
            } satisfies ContextMenuItem,
          ]
        : []),
      {
        label: t("slideSense.delete"),
        icon: <Trash2 className="h-4 w-4" />,
        onClick: () => handleDeleteDeck(deck),
        destructive: true,
      },
    ]
  }, [
    contextMenu,
    handleStartCreateSubfolder,
    handleUploadTrigger,
    handleBulkImportTrigger,
    handleRenameFolder,
    handleDeleteFolder,
    handleRenameDeck,
    handleDeleteDeck,
    handleRetryDeck,
  ])

  // ---- Orphaned decks (folder_id not in folders list) ----------------------
  const folderIds = useMemo(() => new Set(folders.map((f) => f.id)), [folders])
  const orphanDecks = useMemo(
    () => decks.filter((d) => !folderIds.has(d.folder_id)),
    [decks, folderIds]
  )
  const bulkImportProgress = bulkImportState?.uploadTotal
    ? Math.round((bulkImportState.uploaded / bulkImportState.uploadTotal) * 100)
    : 0
  const bulkImportTerminalItems =
    bulkImportState?.job?.items.filter((item) =>
      ["completed", "failed", "skipped", "conflict", "unchanged"].includes(item.status)
    ).length ?? 0
  const bulkImportProcessingProgress = bulkImportState?.job?.items.length
    ? Math.round((bulkImportTerminalItems / bulkImportState.job.items.length) * 100)
    : 0
  const bulkImportItems = bulkImportState?.job?.items ?? bulkImportState?.preflight.items ?? []
  const bulkImportVisibleItems = bulkImportItems.slice(0, 100)

  // ---- Render ---------------------------------------------------------------
  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      <div className="flex shrink-0 items-center gap-2 border-b px-4 py-2.5">
        <h2 className="text-foreground/80 text-sm font-semibold">{t("slideSense.fileManager")}</h2>
        <div className="flex-1" />

        <Button
          className="h-7 gap-1.5 text-xs"
          size="sm"
          variant="outline"
          onClick={() => setShowRootInput(true)}
        >
          <FolderPlus className="h-3.5 w-3.5" />
          {t("slideSense.newFolder")}
        </Button>
        {isSuperAdmin && (
          <Button
            className="h-7 gap-1.5 text-xs"
            disabled={bulkImportBusy}
            size="sm"
            variant="outline"
            onClick={() => handleBulkImportTrigger(null)}
          >
            {bulkImportBusy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FolderUp className="h-3.5 w-3.5" />
            )}
            {t("slideSense.bulkImport")}
          </Button>
        )}
        <Button
          className="h-7 gap-1.5 text-xs"
          disabled={reprocessAllBusy || reprocessableDecks.length === 0}
          size="sm"
          variant="outline"
          onClick={handleReprocessAll}
        >
          {reprocessAllBusy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RotateCcw className="h-3.5 w-3.5" />
          )}
          {t("slideSense.reprocessAll", { count: reprocessableDecks.length })}
        </Button>
        <Button
          className="h-7 gap-1.5 text-xs"
          size="sm"
          variant="outline"
          onClick={() => {
            if (folders.length === 0) {
              toast.error(t("slideSense.createFolderFirst"))
              return
            }
            // If there's only one root folder, upload directly to it
            const rootFolders = folders.filter((f) => !f.parent_folder_id)
            if (rootFolders.length === 1) {
              handleUploadTrigger(rootFolders[0].id)
            } else {
              toast.info(t("slideSense.clickUploadIcon"))
            }
          }}
        >
          <Upload className="h-3.5 w-3.5" />
          {t("slideSense.upload")}
        </Button>
      </div>

      {/* Hidden file input for uploads */}
      <input
        ref={fileInputRef}
        accept=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
        className="hidden"
        type="file"
        onChange={handleFileSelected}
      />
      <input
        ref={bulkFolderInputRef}
        multiple
        accept=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
        className="hidden"
        type="file"
        onChange={handleBulkFolderSelected}
      />

      {/* Tree body */}
      <ScrollArea className="flex-1">
        <div className="py-1.5">
          {/* Root folder inline creation */}
          {showRootInput && (
            <div className="flex items-center gap-1.5 px-2 py-1">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                <ChevronRight className="h-3.5 w-3.5 opacity-0" />
              </span>
              <FolderPlus className="text-warning-ink/60 h-[18px] w-[18px] shrink-0" />
              <Input
                ref={rootInputRef}
                // `focus-visible:ring-1` pins the width: these 24px inline
                // rename fields own their focus affordance (a compact 1px
                // primary ring), so they must not inherit the 3px glow.
                className="ring-primary/40 focus-visible:ring-primary h-6 flex-1 border-none bg-transparent px-1 py-0 text-sm shadow-none ring-1 focus-visible:ring-1"
                disabled={isCreatingRoot}
                maxLength={100}
                placeholder={t("slideSense.folderName")}
                value={rootFolderName}
                onChange={(e) => setRootFolderName(e.target.value)}
                onBlur={() => {
                  setTimeout(() => {
                    if (!rootFolderName.trim()) {
                      setShowRootInput(false)
                      setRootFolderName("")
                    }
                  }, 150)
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void handleCreateRootFolder()
                  if (e.key === "Escape") {
                    setShowRootInput(false)
                    setRootFolderName("")
                  }
                }}
              />
              <button
                className="text-primary text-[11px] font-medium hover:underline"
                disabled={isCreatingRoot}
                type="button"
                onClick={handleCreateRootFolder}
              >
                {isCreatingRoot ? t("slideSense.creating") : t("slideSense.create")}
              </button>
              <button
                className="text-muted-foreground text-[11px] hover:underline"
                type="button"
                onClick={() => {
                  setShowRootInput(false)
                  setRootFolderName("")
                }}
              >
                {t("slideSense.cancel")}
              </button>
            </div>
          )}

          {/* The tree — wrapped in a single DndContext so a folder dragged at
              any depth can find its siblings via the per-level SortableContexts
              nested in FolderRow. Activation distance of 8px keeps clicks on
              the chevron / row body intact. */}
          {tree.length === 0 && !showRootInput ? (
            <div className="text-muted-foreground flex flex-col items-center justify-center gap-3 py-16">
              <Folder className="text-muted-foreground/30 h-10 w-10" />
              <p className="text-sm">{t("slideSense.noFolders")}</p>
              <p className="text-muted-foreground/60 text-xs">{t("slideSense.noFoldersHint")}</p>
            </div>
          ) : (
            <DndContext
              collisionDetection={closestCenter}
              sensors={dndSensors}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={rootIds} strategy={verticalListSortingStrategy}>
                {tree.map((node) => (
                  <FolderRow
                    key={node.folder.id}
                    creatingInFolderId={creatingInFolderId}
                    deckCovers={deckCovers}
                    deckMap={deckMap}
                    expandedIds={expandedIds}
                    level={0}
                    newSubfolderName={newSubfolderName}
                    node={node}
                    uploadingFolderId={uploadingFolderId}
                    usageData={usageData}
                    onCancelCreateSubfolder={handleCancelCreateSubfolder}
                    onConfirmCreateSubfolder={handleConfirmCreateSubfolder}
                    onContextMenuDeck={handleContextMenuDeck}
                    onContextMenuFolder={handleContextMenuFolder}
                    onCreateSubfolder={handleStartCreateSubfolder}
                    onDeleteDeck={handleDeleteDeck}
                    onDeleteFolder={handleDeleteFolder}
                    onFileDrop={handleFileDrop}
                    onNewSubfolderNameChange={setNewSubfolderName}
                    onOpenDrawer={handleOpenDrawer}
                    onRenameDeck={handleRenameDeck}
                    onRenameFolder={handleRenameFolder}
                    onRetryDeck={handleRetryDeck}
                    onToggleExpand={onToggleExpand}
                    onUploadTrigger={handleUploadTrigger}
                    onViewDeck={handleViewDeck}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}

          {/* Orphaned decks (not in any known folder) */}
          {orphanDecks.length > 0 && (
            <div className="mt-3 border-t pt-2">
              <div className="text-muted-foreground/60 px-4 py-1 text-[11px] font-medium tracking-wider uppercase">
                {t("slideSense.unassignedDecks")}
              </div>
              {orphanDecks.map((deck) => (
                <DeckRow
                  key={deck.id}
                  deck={deck}
                  level={0}
                  onContextMenu={handleContextMenuDeck}
                  onDelete={handleDeleteDeck}
                />
              ))}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Footer summary */}
      <div className="text-muted-foreground shrink-0 border-t px-4 py-2 text-[11px]">
        {t("slideSense.folderCount", { count: folders.length })} ·{" "}
        {t("slideSense.deckCount", { count: decks.length })}
      </div>

      {/* Context menu */}
      {contextMenu && (
        <ContextMenu items={contextMenuItems} menu={contextMenu} onClose={closeContextMenu} />
      )}

      {/* Deck slides viewer dialog */}
      <Dialog open={viewingDeck !== null} onOpenChange={(open) => !open && setViewingDeck(null)}>
        <DialogContent className="max-h-[85vh] max-w-4xl">
          <DialogHeader>
            <DialogTitle>{viewingDeck?.original_filename}</DialogTitle>
            <DialogDescription>
              {t("slideSense.slidesInPresentation", { count: viewingDeck?.slide_count ?? 0 })}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[65vh]">
            {isDeckSlidesLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
              </div>
            )}
            {!isDeckSlidesLoading && deckSlides.length === 0 && (
              <div className="text-muted-foreground flex items-center justify-center py-12 text-sm">
                {t("slideSense.noSlidesFound")}
              </div>
            )}
            {!isDeckSlidesLoading && deckSlides.length > 0 && (
              <div className="grid grid-cols-2 gap-3 p-1 sm:grid-cols-3 lg:grid-cols-4">
                {deckSlides.map((slide) => (
                  <div
                    key={slide.id}
                    className="border-border/50 bg-card overflow-hidden rounded-lg border shadow-sm"
                  >
                    <div className="bg-muted/40 relative aspect-[16/9] w-full overflow-hidden">
                      <SlideThumbnailImage slideId={slide.id} thumbnailUrl={slide.thumbnail_url} />
                      <div className="absolute top-1.5 left-1.5">
                        <Badge className="text-[10px]" variant="secondary">
                          #{slide.slide_index + 1}
                        </Badge>
                      </div>
                    </div>
                    <div className="space-y-1 p-2">
                      <p className="truncate text-xs font-medium">
                        {slide.ai_title ||
                          t("slideSense.present.slideFallback", { n: slide.slide_index + 1 })}
                      </p>
                      <div className="flex items-center gap-1">
                        <SlideCategoryBadge category={slide.category} />
                      </div>
                      {slide.ai_summary && (
                        <p className="text-muted-foreground line-clamp-2 text-[10px]">
                          {slide.ai_summary}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Rename dialog (folder or deck) */}
      <Dialog
        open={!!renameTarget}
        onOpenChange={(open) => {
          if (!open) closeRenameDialog()
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {renameTarget?.kind === "folder"
                ? t("slideSense.renameFolderTitle")
                : t("slideSense.renameDeckTitle")}
            </DialogTitle>
            <DialogDescription>
              {renameTarget?.kind === "folder"
                ? t("slideSense.renameFolderPrompt")
                : t("slideSense.renameDeckPrompt")}
            </DialogDescription>
          </DialogHeader>
          <Input
            disabled={renameBusy}
            maxLength={renameTarget?.kind === "folder" ? 100 : 255}
            value={renameValue}
            aria-label={
              renameTarget?.kind === "folder"
                ? t("slideSense.renameFolderTitle")
                : t("slideSense.renameDeckTitle")
            }
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                void confirmRename()
              }
            }}
          />
          <DialogFooter>
            <Button disabled={renameBusy} variant="outline" onClick={closeRenameDialog}>
              {t("slideSense.cancel")}
            </Button>
            <Button
              disabled={renameBusy || !renameValue.trim()}
              onClick={() => void confirmRename()}
            >
              {renameBusy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("slideSense.renaming")}
                </>
              ) : (
                t("slideSense.rename")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete-deck confirmation dialog */}
      <AlertDialog
        open={!!deleteDeckTarget}
        onOpenChange={(open) => {
          if (!open && !deleteDeckBusy) setDeleteDeckTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("slideSense.deleteDeckTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDeckTarget
                ? t("slideSense.deleteDeckConfirm", {
                    name: deleteDeckTarget.original_filename,
                  })
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteDeckBusy}>
              {t("slideSense.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteDeckBusy}
              onClick={(e) => {
                e.preventDefault()
                void confirmDeleteDeck()
              }}
            >
              {t("slideSense.deletePermanently")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!reprocessDeckTarget}
        onOpenChange={(open) => {
          if (!open && !reprocessDeckBusy) setReprocessDeckTarget(null)
        }}
      >
        <AlertDialogContent data-testid="reprocess-deck-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("slideSense.reprocessTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {reprocessDeckTarget
                ? t("slideSense.reprocessConfirm", {
                    name: reprocessDeckTarget.original_filename,
                    count: reprocessDeckTarget.slide_count,
                  })
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reprocessDeckBusy}>
              {t("slideSense.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={reprocessDeckBusy}
              onClick={(e) => {
                e.preventDefault()
                void confirmReprocessDeck()
              }}
            >
              {reprocessDeckBusy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("slideSense.reprocess")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={reprocessAllOpen}
        onOpenChange={(open) => {
          if (!open && !reprocessAllBusy) setReprocessAllOpen(false)
        }}
      >
        <AlertDialogContent data-testid="reprocess-all-decks-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("slideSense.reprocessAllTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("slideSense.reprocessAllConfirm", { count: reprocessableDecks.length })}
              {reprocessAllProgress ? (
                <span className="text-foreground mt-3 block text-sm font-medium">
                  {t("slideSense.reprocessAllProgress", reprocessAllProgress)}
                </span>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reprocessAllBusy}>
              {t("slideSense.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={reprocessAllBusy}
              onClick={(e) => {
                e.preventDefault()
                void confirmReprocessAll()
              }}
            >
              {reprocessAllBusy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("slideSense.reprocessAll", { count: reprocessableDecks.length })
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open && !deleteBusy) setDeleteTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("slideSense.deleteFolderTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("slideSense.deleteFolderWarning")}
              {deleteTarget?.preview && (
                <>
                  {" "}
                  {t("slideSense.deleteFolderCount.folders", {
                    count: deleteTarget.preview.folders,
                  })}
                  {", "}
                  {t("slideSense.deleteFolderCount.decks", {
                    count: deleteTarget.preview.decks,
                  })}
                  {", "}
                  {t("slideSense.deleteFolderCount.slides", {
                    count: deleteTarget.preview.slides,
                  })}
                  {". "}
                  {t("slideSense.deleteFolderCount.usersAffected", {
                    count: deleteTarget.preview.affected_users,
                  })}
                </>
              )}
              {!deleteTarget?.preview && (
                <span className="text-muted-foreground ml-1 inline-flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteBusy}>{t("slideSense.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteBusy}
              onClick={(e) => {
                e.preventDefault()
                void confirmDeleteFolder()
              }}
            >
              {t("slideSense.deletePermanently")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={!!bulkImportState}
        onOpenChange={(open) => {
          if (!open && !bulkImportBusy) setBulkImportState(null)
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("slideSense.bulkImportTitle")}</DialogTitle>
            <DialogDescription>
              {bulkImportState
                ? t("slideSense.bulkImportDescription", {
                    name: bulkImportState.manifest.rootFolderName,
                  })
                : ""}
            </DialogDescription>
          </DialogHeader>

          {bulkImportState && (
            <div className="space-y-4">
              <div
                className={cn(
                  "grid gap-2",
                  // Once processing has started we surface a "Failed" tile
                  // alongside the preflight categories. Preflight itself
                  // only shows the 5-column manifest summary.
                  bulkImportState.phase === "preflight" ? "grid-cols-5" : "grid-cols-6"
                )}
              >
                {(["new", "changed", "unchanged", "skipped", "conflict"] as const).map((key) => (
                  <div key={key} className="rounded-md border p-2">
                    <div className="text-muted-foreground text-[11px] uppercase">
                      {t(`slideSense.bulkImportSummary.${key}`)}
                    </div>
                    <div className="text-lg font-semibold">
                      {bulkImportState.preflight.summary[key]}
                    </div>
                  </div>
                ))}
                {bulkImportState.phase !== "preflight" &&
                  (() => {
                    const failedCount =
                      bulkImportState.job?.items.filter((it) => it.status === "failed").length ?? 0
                    return (
                      <div
                        className={cn(
                          "rounded-md border p-2",
                          failedCount > 0 && "border-destructive/30 bg-destructive/5"
                        )}
                      >
                        <div
                          className={cn(
                            "text-[11px] uppercase",
                            failedCount > 0 ? "text-destructive" : "text-muted-foreground"
                          )}
                        >
                          {t("slideSense.bulkImportSummary.failed")}
                        </div>
                        <div
                          className={cn(
                            "text-lg font-semibold",
                            failedCount > 0 && "text-destructive"
                          )}
                        >
                          {failedCount}
                        </div>
                      </div>
                    )
                  })()}
              </div>

              {bulkImportState.phase === "preflight" &&
                bulkImportState.preflight.summary.conflict > 0 && (
                  <div className="text-destructive flex items-center gap-2 rounded-md border p-3 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    {t("slideSense.bulkImportHasConflicts")}
                  </div>
                )}

              {bulkImportState.phase === "uploading" && (
                <div className="space-y-2">
                  <div className="text-muted-foreground flex justify-between text-sm">
                    <span>{t("slideSense.bulkImportUploading")}</span>
                    <span>
                      {bulkImportState.uploaded} / {bulkImportState.uploadTotal}
                    </span>
                  </div>
                  <Progress value={bulkImportProgress} />
                </div>
              )}

              {(bulkImportState.phase === "processing" ||
                bulkImportState.phase === "completed") && (
                <div className="space-y-2">
                  <div className="text-muted-foreground flex justify-between text-sm">
                    <span>{t("slideSense.bulkImportProcessing")}</span>
                    <span>
                      {bulkImportTerminalItems} / {bulkImportState.job?.items.length ?? 0}
                    </span>
                  </div>
                  <Progress value={bulkImportProcessingProgress} />
                </div>
              )}

              {/* Loud, persistent failure banner. The yellow `completed_with_errors`
                  toast vanishes after a few seconds and the per-row badges are
                  easy to miss in a long list — this surfaces "X failed" right
                  under the progress bar with a Retry-all hint, so the dialog
                  doesn't quietly close with broken items inside. */}
              {bulkImportState.phase === "completed" &&
                (() => {
                  const failedCount =
                    bulkImportState.job?.items.filter((it) => it.status === "failed").length ?? 0
                  if (failedCount === 0) return null
                  return (
                    <div className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border p-3 text-sm">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      <div className="flex-1">
                        <div className="font-medium">
                          {t("slideSense.bulkImportFailedBanner", { count: failedCount })}
                        </div>
                        <div className="text-destructive/80 mt-0.5 text-xs">
                          {t("slideSense.bulkImportFailedHint")}
                        </div>
                      </div>
                    </div>
                  )
                })()}

              <div className="text-muted-foreground flex items-center justify-between text-xs">
                <span>
                  {t("slideSense.bulkImportFileCount", { count: bulkImportItems.length })}
                </span>
                {bulkImportItems.length > bulkImportVisibleItems.length && (
                  <span>
                    {t("slideSense.bulkImportShowing", {
                      shown: bulkImportVisibleItems.length,
                      total: bulkImportItems.length,
                    })}
                  </span>
                )}
              </div>

              <div className="h-80 max-h-[45vh] overflow-y-auto rounded-md border">
                <div className="divide-y">
                  {bulkImportVisibleItems.map((item) => {
                    // Retry is only meaningful for items that finished failed
                    // AND have a deck_id (the backend creates the deck row
                    // before processing, so the id should always be there for
                    // status="failed", but be defensive).
                    const canRetry =
                      bulkImportState.phase !== "preflight" &&
                      item.status === "failed" &&
                      Boolean(item.deck_id)
                    const isRetrying = bulkRetryingIds.has(item.id)
                    const isFailed = item.status === "failed"
                    return (
                      <div
                        key={item.id}
                        className={cn(
                          "flex min-w-0 items-start gap-3 px-3 py-2 text-sm",
                          isFailed && "bg-destructive/5"
                        )}
                      >
                        {isFailed ? (
                          <AlertCircle className="text-destructive mt-0.5 h-4 w-4 shrink-0" />
                        ) : (
                          <FileText className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="overflow-x-auto overscroll-x-contain pb-1">
                            <span
                              className={cn(
                                "whitespace-nowrap",
                                isFailed && "text-foreground font-medium"
                              )}
                            >
                              {item.relative_path}
                            </span>
                          </div>
                          {/* Surface the backend-recorded error so the user sees
                              *why* an item failed without digging through logs.
                              Truncate with the title attribute exposing the full
                              text on hover — orchestrator errors can be long. */}
                          {isFailed && item.processing_error && (
                            <div
                              className="text-destructive/90 mt-0.5 truncate text-xs"
                              title={item.processing_error}
                            >
                              {item.processing_error}
                            </div>
                          )}
                        </div>
                        <Badge
                          className="mt-0.5 max-w-28 shrink-0 truncate text-[10px] capitalize"
                          variant={
                            item.action === "conflict" || isFailed ? "destructive" : "secondary"
                          }
                        >
                          {bulkImportState.phase === "preflight"
                            ? t(`slideSense.bulkImportSummary.${item.action}`)
                            : t(`slideSense.bulkImportStatus.${item.status}`)}
                        </Badge>
                        {canRetry && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Radix tooltip-on-disabled-control wrapper must stay focusable */}
                              <span tabIndex={isRetrying ? 0 : -1}>
                                <Button
                                  aria-label={t("slideSense.retry")}
                                  className="text-muted-foreground hover:text-foreground -mt-0.5 size-7 shrink-0"
                                  disabled={isRetrying}
                                  size="icon"
                                  type="button"
                                  variant="ghost"
                                  onClick={() => void handleRetryBulkItem(item)}
                                >
                                  {isRetrying ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <RotateCcw className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{t("slideSense.retry")}</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              disabled={
                bulkImportBusy ||
                bulkImportState?.phase === "uploading" ||
                bulkImportState?.phase === "processing"
              }
              onClick={() => setBulkImportState(null)}
            >
              {bulkImportState?.phase === "completed"
                ? t("slideSense.close")
                : t("slideSense.cancel")}
            </Button>
            {bulkImportState?.phase === "preflight" && (
              <Button
                disabled={
                  bulkImportBusy ||
                  bulkImportState.preflight.summary.conflict > 0 ||
                  bulkImportState.uploadTotal === 0
                }
                onClick={() => void startBulkImport()}
              >
                {bulkImportBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("slideSense.bulkImportStart")}
              </Button>
            )}
            {bulkImportState?.phase === "completed" &&
              (() => {
                const failedCount =
                  bulkImportState.job?.items.filter((it) => it.status === "failed" && it.deck_id)
                    .length ?? 0
                if (failedCount === 0) return null
                const anyRetrying = bulkRetryingIds.size > 0
                return (
                  <Button disabled={anyRetrying} onClick={() => void handleRetryAllFailedBulk()}>
                    {anyRetrying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    <RotateCcw className="mr-1 h-3.5 w-3.5" />
                    {t("slideSense.retryAllFailed", { count: failedCount })}
                  </Button>
                )
              })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!largeDeckPrompt}
        onOpenChange={(open) => {
          if (!open) setLargeDeckPrompt(null)
        }}
      >
        <AlertDialogContent data-testid="large-deck-warning">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-warning-ink h-5 w-5" />
              {largeDeckPrompt
                ? t("slideSense.largeDeckWarningTitle", { count: largeDeckPrompt.slideCount })
                : ""}
            </AlertDialogTitle>
            <AlertDialogDescription>{t("slideSense.largeDeckWarningBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("slideSense.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!largeDeckPrompt) return
                const { file, folderId } = largeDeckPrompt
                setLargeDeckPrompt(null)
                await performUpload(file, folderId)
              }}
            >
              {t("slideSense.upload")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Usage node drawer */}
      <UsageNodeDrawer
        id={drawer?.id ?? null}
        type={drawer?.type ?? null}
        onClose={() => setDrawer(null)}
      />
    </div>
  )
}
